import { forwardRef, useState } from 'react'
import {
  InputAdornment,
  InputBase,
  InputBaseProps,
  SxProps,
} from '@mui/material'
import DatePicker, { registerLocale } from 'react-datepicker'
import { styled } from '@mui/system'
import { PopperContainer } from '../../inputs/DatePicker'
import { DatePickerCustomHeader } from '../../inputs/DatePicker/DatePickerCustomHeader'
import el from 'date-fns/locale/el'
import ja from 'date-fns/locale/ja'
import {
  formatDateForApi,
  getFiscalYearNumber,
  getQuarterNumber,
} from '../../../../utils/date'
import DateVO from '../../../../vo/DateVO'
import { EventNote } from '@mui/icons-material'
import { FontSize, TextColor } from '../../../../styles/commonStyles'
import { colorPalette } from '../../../style/colorPalette'
import './DateSelectStyle.scss'

registerLocale('en', el)
registerLocale('ja', ja)

export enum DateSelectType {
  YEAR_MONTH,
  DATE,
  WEEK,
  QUARTER,
}

const getDateFormat = (type: DateSelectType) => {
  switch (type) {
    case DateSelectType.YEAR_MONTH:
      return 'yyyy/MM'
    case DateSelectType.DATE:
      return 'yyyy/MM/dd'
    case DateSelectType.WEEK:
      return 'yyyy/MM/dd'
    case DateSelectType.QUARTER:
      return 'yyyy/QQQ'
  }
}

const getPickerMode = (type: DateSelectType) => {
  return {
    showMonthYearPicker: type === DateSelectType.YEAR_MONTH,
    showQuarterYearPicker: type === DateSelectType.QUARTER,
  }
}

const getWeekStart = (date: Date, weekStartDay: number) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = (day - weekStartDay + 7) % 7
  d.setDate(d.getDate() - diff)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

interface Props {
  type: DateSelectType
  value: DateVO | undefined
  placeholder?: string
  onChange: (value: string | undefined) => void
  startDate?: DateVO
  endDate?: DateVO
  disabled?: boolean
  sx?: SxProps
  targetMonth?: DateVO
  calendarStartDay?: number
  fiscalStartMonth?: number
  /**
   * 予定開始日・終了日のセルエディタと同じ年月ドロップダウン付きカレンダーにする。
   * ドロップダウンの月名が英語のため、曜日表記が混在しないようロケール指定も外す
   */
  showYearMonthDropdown?: boolean
}

const RootContainer = styled('div')({
  width: '100%',
  border: `solid 1px ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 3px',
  zIndex: 3,
})
const CalendarIcon = styled(EventNote)({
  color: TextColor.GREY,
  fontSize: FontSize.MEDIUM,
})
const DateInputBase = styled(InputBase)({
  fontSize: '11px !important',
  color: colorPalette.monotone[4],
})

type FiscalAwareDateInputProps = InputBaseProps & {
  formatDisplayValue: (value: string) => string
}

const FiscalAwareDateInput = forwardRef<
  HTMLInputElement,
  FiscalAwareDateInputProps
>(({ formatDisplayValue, value, ...rest }, ref) => (
  <DateInputBase
    ref={ref}
    value={typeof value === 'string' ? formatDisplayValue(value) : value}
    {...rest}
  />
))

const DateSelect = (props: Props) => {
  const {
    type,
    value,
    placeholder,
    onChange,
    startDate,
    endDate,
    disabled,
    sx,
    targetMonth,
    calendarStartDay,
    fiscalStartMonth,
    showYearMonthDropdown,
  } = props
  const dateFormat = getDateFormat(type)
  const pickerMode = getPickerMode(type)
  const [displayedYear, setDisplayedYear] = useState(
    value ? value.toDate().getFullYear() : new Date().getFullYear()
  )

  const fiscalQuarterProps =
    type === DateSelectType.QUARTER && fiscalStartMonth
      ? {
          renderQuarterContent: (_quarter: number, shortQuarter: string) => {
            const calendarQuarter = parseInt(shortQuarter.replace('Q', ''), 10)
            const firstMonth = (calendarQuarter - 1) * 3 + 1
            const lastMonth = calendarQuarter * 3
            const fq = getQuarterNumber(firstMonth, fiscalStartMonth)
            const calendarYear = displayedYear
            const fy = getFiscalYearNumber(
              firstMonth,
              fiscalStartMonth,
              calendarYear
            )
            const shortFy = String(fy).slice(-2)
            return (
              <span>
                {shortFy}/Q{fq}
                <br />（{firstMonth}-{lastMonth}月）
              </span>
            )
          },
        }
      : {}

  const formatDisplayValue = (formatted: string) => {
    if (type !== DateSelectType.QUARTER || !fiscalStartMonth || !value) {
      return formatted
    }
    const month = value.toDate().getMonth() + 1
    const q = getQuarterNumber(month, fiscalStartMonth)
    const fiscalYear = getFiscalYearNumber(
      month,
      fiscalStartMonth,
      value.toDate().getFullYear()
    )
    return `${fiscalYear}/Q${q}`
  }
  const locale = navigator.language.split(/[-_]/)[0]

  // 月グリッド・四半期グリッドでは月ドロップダウンが重複するため日付グリッドのみ
  const isDayGrid =
    !pickerMode.showMonthYearPicker && !pickerMode.showQuarterYearPicker
  const customHeader =
    showYearMonthDropdown && isDayGrid ? DatePickerCustomHeader : undefined

  const getTargetMonthLimits = () => {
    if (!targetMonth) {
      return { minDate: undefined, maxDate: undefined }
    }
    const targetDate = targetMonth.toDate()
    const year = targetDate.getFullYear()
    const month = targetDate.getMonth()
    return {
      minDate: new Date(year, month, 1),
      maxDate: new Date(year, month + 1, 0),
    }
  }

  const { minDate, maxDate } = getTargetMonthLimits()

  return (
    <RootContainer sx={sx}>
      <DatePicker
        // TODO use locale of environment
        disabled={disabled}
        locale={customHeader ? undefined : locale}
        renderCustomHeader={customHeader}
        selected={value && value.toDate()}
        startDate={startDate ? startDate.toDate() : undefined}
        endDate={endDate ? endDate.toDate() : undefined}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat={dateFormat}
        {...pickerMode}
        {...(type === DateSelectType.WEEK &&
          value && {
            dayClassName: (date: Date) =>
              getWeekStart(date, calendarStartDay ?? 0) ===
              getWeekStart(value.toDate(), calendarStartDay ?? 0)
                ? 'react-datepicker__day--selected'
                : '',
          })}
        onChange={date => {
          onChange(formatDateForApi(date))
        }}
        placeholderText={placeholder || ''}
        onYearChange={(date: Date) => setDisplayedYear(date.getFullYear())}
        {...fiscalQuarterProps}
        {...(type === DateSelectType.QUARTER &&
          fiscalStartMonth && {
            calendarClassName: 'fiscal-quarter-picker',
          })}
        customInput={
          <FiscalAwareDateInput
            formatDisplayValue={formatDisplayValue}
            endAdornment={
              <InputAdornment
                position="end"
                style={{ marginRight: 0, marginLeft: 0 }}
              >
                <CalendarIcon />
              </InputAdornment>
            }
          />
        }
        popperPlacement="bottom-start"
        popperModifiers={[
          {
            name: 'offset',
            options: { offset: [20, 0] },
          },
          {
            name: 'preventOverflow',
            enabled: true,
          },
        ]}
        popperContainer={PopperContainer}
        fixedHeight={true}
      />
    </RootContainer>
  )
}

export default DateSelect
